import { FormEvent, ReactNode, useState } from "react"
import { Head, Link, router, useForm } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { FeedbackStatusBadge } from "@/components/feedback/FeedbackStatusBadge"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Select } from "@/components/ui/select"
import { FEEDBACK_CATEGORIES, FEEDBACK_STATUSES, type FeedbackDetail, type FeedbackStatus } from "@/types/feedback"

export default function AdminFeedbackShow({ feedback }: { feedback: FeedbackDetail }) {
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const form = useForm({
    status: feedback.status,
    admin_notes: feedback.admin_notes,
    notify_user: false,
    resolution_note: "",
  })
  const replyForm = useForm({ body: "" })

  const completing = form.data.status === "completed" && feedback.status !== "completed"
  const canEmail = !!feedback.reply_email

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.transform((data) => ({
      feedback: { status: data.status, admin_notes: data.admin_notes },
      notify_user: completing && data.notify_user,
      resolution_note: data.resolution_note,
    }))
    form.patch(`/admin/feedbacks/${feedback.id}`, { preserveScroll: true, onSuccess: () => form.setData("notify_user", false) })
  }

  const sendReply = (e: FormEvent) => {
    e.preventDefault()
    replyForm.post(`/admin/feedbacks/${feedback.id}/reply`, { preserveScroll: true, onSuccess: () => replyForm.reset() })
  }

  const destroy = () => router.delete(`/admin/feedbacks/${feedback.id}`)

  const title = `Feedback #${feedback.id}`
  const category = FEEDBACK_CATEGORIES.find((c) => c.value === feedback.category)?.label ?? feedback.category

  return (
    <>
      <Head title={title}>
        <meta name="description" content={`Review feedback #${feedback.id}, reply to the sender, and update its status.`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={`Review feedback #${feedback.id}, reply to the sender, and update its status.`} />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>{title}</h1>
              <p className="mt-1">Received {formatDateTime(feedback.created_at)}</p>
            </div>
            <div className="flex items-center gap-3">
              <Badge tone="neutral">{category}</Badge>
              <FeedbackStatusBadge status={feedback.status} />
              <Link href="/admin/feedbacks" className="text-sm text-ink-muted no-underline hover:text-ink-display">
                Back to feedback
              </Link>
            </div>
          </div>
        </div>

        <section className="callout mt-6" aria-label="Message">
          <p className="whitespace-pre-wrap">{feedback.message}</p>
        </section>

        {feedback.has_screenshot && (
          <section className="mt-6" aria-label="Screenshot">
            <h2>Screenshot</h2>
            <a href={`/admin/feedbacks/${feedback.id}/screenshot`} target="_blank" rel="noreferrer" className="mt-2 inline-block">
              <img
                src={`/admin/feedbacks/${feedback.id}/screenshot`}
                alt="Screenshot attached to this feedback"
                className="max-h-96 max-w-full rounded-md border border-hairline"
              />
            </a>
          </section>
        )}

        <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-[max-content_1fr]">
          <DetailRow label="From">
            {feedback.user_id ? (
              <Link href={`/admin/users/${feedback.user_id}`}>{feedback.user_email}</Link>
            ) : feedback.contact_email ? (
              <>Signed out · {feedback.contact_email}</>
            ) : (
              "Signed out"
            )}
          </DetailRow>
          <DetailRow label="Page">
            {feedback.page_path ? <code>{feedback.page_path}</code> : "Unknown"}
            {feedback.page_title ? ` — ${feedback.page_title}` : ""}
          </DetailRow>
          <DetailRow label="Browser">{feedback.user_agent ?? "Unknown"}</DetailRow>
        </dl>

        <form onSubmit={submit} className="mt-8 max-w-md space-y-4">
          <div className="space-y-2">
            <label htmlFor="feedback-status">Status</label>
            <Select
              id="feedback-status"
              value={form.data.status}
              onChange={(e) => form.setData("status", e.target.value as FeedbackStatus)}
            >
              {FEEDBACK_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Select>
          </div>

          {completing && canEmail && (
            <div className="callout space-y-3">
              <label className="flex cursor-pointer items-start gap-3 font-normal text-ink-body">
                <Checkbox
                  className="mt-1"
                  checked={form.data.notify_user}
                  onChange={(e) => form.setData("notify_user", e.target.checked)}
                />
                <span>
                  Email {feedback.reply_email} that this is fixed
                  <span className="block text-xs text-ink-muted">Sends a short &ldquo;we fixed it&rdquo; note.</span>
                </span>
              </label>
              {form.data.notify_user && (
                <div className="space-y-2">
                  <label htmlFor="feedback-resolution-note">Add a personal note (optional)</label>
                  <textarea
                    id="feedback-resolution-note"
                    className="form-control form-control-textarea"
                    rows={3}
                    value={form.data.resolution_note}
                    onChange={(e) => form.setData("resolution_note", e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="feedback-notes">Notes (only admins see these)</label>
            <textarea
              id="feedback-notes"
              className="form-control form-control-textarea"
              rows={4}
              value={form.data.admin_notes}
              onChange={(e) => form.setData("admin_notes", e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3">
            <Button type="submit" disabled={form.processing}>
              Save
            </Button>
            <Button type="button" variant="ghost" onClick={() => setConfirmingDelete(true)}>
              Delete
            </Button>
          </div>
        </form>

        <section className="mt-10 max-w-2xl" aria-label="Replies">
          <h2>Replies</h2>
          {feedback.replies.length === 0 ? (
            <p className="mt-2 text-sm text-ink-muted">No emails sent about this feedback yet.</p>
          ) : (
            <ul className="mt-3 space-y-3">
              {feedback.replies.map((reply) => (
                <li key={reply.id} className="callout">
                  <p className="text-xs text-ink-muted">
                    {reply.kind === "resolved" ? "“We fixed it” note" : "Reply"} to {reply.to_email}
                    {reply.sender_email ? ` from ${reply.sender_email}` : ""} · {formatDateTime(reply.created_at)}
                  </p>
                  {reply.body && <p className="mt-2 whitespace-pre-wrap">{reply.body}</p>}
                </li>
              ))}
            </ul>
          )}

          {canEmail ? (
            <form onSubmit={sendReply} className="mt-4 max-w-md space-y-2">
              <label htmlFor="feedback-reply">Reply by email to {feedback.reply_email}</label>
              <textarea
                id="feedback-reply"
                className="form-control form-control-textarea"
                rows={4}
                required
                value={replyForm.data.body}
                onChange={(e) => replyForm.setData("body", e.target.value)}
              />
              <Button type="submit" disabled={replyForm.processing || replyForm.data.body.trim() === ""}>
                Send reply
              </Button>
            </form>
          ) : (
            <p className="mt-4 text-sm text-ink-muted">
              This visitor was signed out and didn&rsquo;t leave an email address, so there&rsquo;s no one to reply to.
            </p>
          )}
        </section>

        <Dialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete this feedback?</DialogTitle>
              <DialogDescription>This permanently removes the message, screenshot, replies, and your notes.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setConfirmingDelete(false)}>
                Cancel
              </Button>
              <Button type="button" variant="danger" onClick={destroy}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </AdminShell>
    </>
  )
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd className="min-w-0 break-words text-sm text-ink-body">{children}</dd>
    </>
  )
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
